import { defineComponent as _defineComponent } from 'vue'
import { computed, nextTick, ref, watch } from 'vue';
import { useRoute } from 'vue-router/composables';
import { Route } from 'vue-router';
import { storeToRefs } from 'pinia';

import BasicButton from '@/ui-components/BasicButton.vue';
import { CollectionType } from '@/enums/collection-type';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { NewPageType } from '@/@types/graphql-types';
import { isAnyFilterActive } from '@/helpers/filter-helper';
import { useFilterStore } from '@/stores/pinia/useFilterStore';
import type { FilterName } from '@/stores/useFilterStoreTypes';
import { faChevronUp, faChevronDown, faFilter } from '@fortawesome/free-solid-svg-icons';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';


export default /*#__PURE__*/_defineComponent({
  __name: 'ContentHeader',
  props: {
    lockedCollectionType: null,
    personId: { default: null },
    personBackdrop: { default: null },
    isSearchView: { type: Boolean, default: false },
    showFilterButton: { type: Boolean, default: false },
    isFiltersVisible: { type: Boolean, default: false }
  },
  emits: ['update:isFiltersVisible', 'toggle'],
  setup(__props: any, { emit }) {

const props = __props as { lockedCollectionType: CollectionType, personId: string | null, personBackdrop: string | null, isSearchView: boolean, showFilterButton: boolean, isFiltersVisible: boolean };

const ContentHeaderPersonDetails = () =>
	import(
		/* webpackChunkName: "content-header-person" */ '@/components/content-header/ContentHeaderPersonDetails.vue'
	);





const expanded = ref(false);
const isFilterContent = ref(false);
const isMadeInEurope = ref(false);
const lockedUrlMetadata: any = ref(null);
const query = ref({} as Route['query']);

const { activeRoute, urlMetadata, urlMetadataPromise } = storeToRefs(useRoutingStore());
const { currentFilters } = storeToRefs(useFilterStore());
const { isLoggedIn } = storeToRefs(useUserStore());
const route = useRoute();

function onUrlMetadataChange() {
	if (
		(activeRoute.value?.meta?.collectionType === props.lockedCollectionType ||
			props.lockedCollectionType === 'subgenre') &&
		// there's a brief time when urlMetadata hasn't been fetched yet where we'd use the urlMetadata of the previous site, e.g. TitleDetail's.
		// this is to prevent unwanted text flashes caused by "outdated" urlMetadata.
		urlMetadata?.value?.objectType === 'PAGE'
	) {
		lockedUrlMetadata.value = urlMetadata.value;

		query.value = route.query;
		isMadeInEurope.value = route.name === 'app.titles.popular.european';

		// pageType can be defined as pageType or newPageType on the route meta. PRICE_DROP is a newPageType
		const pageHasFilterContent =
			![NewPageType.PriceDrop].includes(route.meta?.newPageType) &&
			![NewPageType.Upcoming].includes(route.meta?.pageType);

		isFilterContent.value =
			pageHasFilterContent && (hasOtherFiltersActive() || currentFilters.value.providers.length > 1);
	}
}

watch(() => urlMetadata.value, onUrlMetadataChange, { immediate: true });

// main purpose of this watcher is to capture `isFilterContent` changes, that are usually covered by the `urlMetadata` watcher.
// but urlMetadata isn't triggered for only query parameter url changes.
//
// e.g.:
// - you enter the page on /de/Anbieter/netflix
// - you select the action category filter, the url changes to /de/Anbieter/netflix?genres=act
// - this change needs to be captured via the `$route` watcher

async function onRouteChange() {
	try {
		await urlMetadataPromise.value;
		onUrlMetadataChange();
	} catch {}
}

watch(route, onRouteChange);

function toggleFilters() {
	emit('update:isFiltersVisible', !props.isFiltersVisible);
}

// checks if any filters are active that don't cause url changes and h1 text from urlMetadata to change.
function hasOtherFiltersActive() {
	const urlFilters: FilterName[] = [
		'providers',
		'content_types',
		'sort_asc',
		'sort_by',
		'sorting_random_seed',
		'subgenres',
	];
	const filters: FilterName[] = (Object.keys(currentFilters.value) as FilterName[]).filter(
		item => !urlFilters.includes(item)
	);
	return isAnyFilterActive(filters, currentFilters.value) || isSubgenresFilterActive();
}

function isSubgenresFilterActive() {
	return (
		(currentFilters?.value.subgenres || []).length > 0 &&
		!activeRoute.value?.name?.startsWith('app.titles.popular.subgenre')
	);
}

function onToggle() {
	// implemented to recalc filter bar position according to new header height
	nextTick(() => emit('toggle'));
}

function toggleExpand() {
	expanded.value = !expanded.value;
	onToggle();
	const action = expanded.value ? 'expanded' : 'collapsed';
	TrackingHelper.trackEvent('content_header', {
		action,
	});
}

const assetsDir = computed(() => `/${ASSETS_DIR}`);

const h1Title = computed(() => {
	return lockedUrlMetadata.value && lockedUrlMetadata.value.heading1 ? lockedUrlMetadata.value.heading1 : '';
});

const h2Title = computed(() => {
	return lockedUrlMetadata.value && lockedUrlMetadata.value.heading2 ? lockedUrlMetadata.value.heading2 : '';
});

const h1Text = computed(() => {
	return lockedUrlMetadata.value &&
		lockedUrlMetadata.value.htmlContentJson &&
		lockedUrlMetadata.value.htmlContentJson.h1_text
		? lockedUrlMetadata.value.htmlContentJson.h1_text
		: '';
});

const h2Text = computed(() => {
	return lockedUrlMetadata.value &&
		lockedUrlMetadata.value.htmlContentJson &&
		lockedUrlMetadata.value.htmlContentJson.h2_text
		? lockedUrlMetadata.value.htmlContentJson.h2_text
		: '';
});

return { __sfc: true,ContentHeaderPersonDetails, props, emit, expanded, isFilterContent, isMadeInEurope, lockedUrlMetadata, query, activeRoute, urlMetadata, urlMetadataPromise, currentFilters, isLoggedIn, route, onUrlMetadataChange, onRouteChange, toggleFilters, hasOtherFiltersActive, isSubgenresFilterActive, onToggle, toggleExpand, assetsDir, h1Title, h2Title, h1Text, h2Text, BasicButton, faChevronUp, faChevronDown, faFilter }
}

})